import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'components';
import { useSearchGtipQuery } from 'redux/slices/gtip/gtipApi';

const DropdownMessage = ({ text, isError = false }) => (
  <div className={`px-4 py-2 text-sm ${isError ? 'text-red-500' : 'text-gray-500'}`}>
    {text}
  </div>
);

const DropdownItem = ({ result, onSelect }) => (
  <div
    className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
    onClick={() => onSelect(result)}
  >
    <div className="font-medium text-gray-900">{result.showGtip}</div>
    <div className="text-gray-600 text-xs mt-1">{result.gtip}</div>
  </div>
);

const GtipSearchInput = ({
  name,
  value,
  onChange,
  required = false,
  showLabel = true,
  label = 'GTIP',
  errors,
  maxLength = 100,
  onValidationChange,
  ...rest
}) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => setSearchTerm(value || ''), [value]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: searchResults = [],
    isLoading,
    isError,
  } = useSearchGtipQuery(
    { searchTerm: debouncedSearchTerm },
    { skip: !debouncedSearchTerm || debouncedSearchTerm.length < 4 }
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutside = dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                       inputRef.current && !inputRef.current.contains(event.target);
      if (isOutside) setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    if (inputValue.length > maxLength) return;

    setSearchTerm(inputValue);
    onChange(e.target.name, '');
    
    if (inputValue.length === 0) {
      onValidationChange?.(null);
      setIsOpen(false);
    } else if (inputValue.length < 4) {
      onValidationChange?.('GTIP kodu en az 4 haneli olmalıdır');
      setIsOpen(false);
    } else {
      onValidationChange?.('Lütfen geçerli bir GTIP kodu seçiniz');
      setIsOpen(true);
    }
  };

  const handleResultSelect = (result) => {
    const gtipCode = result.description;
    onChange(name, gtipCode);
    setSearchTerm(gtipCode);
    setIsOpen(false);
    onValidationChange?.(null);
  };

  const handleInputFocus = () => {
    if (searchTerm.length >= 4 && searchResults.length > 0) setIsOpen(true);
  };

  return (
    <div className="relative w-full">
      <div ref={inputRef}>
        <Input
          name={name}
          required={required}
          showLabel={showLabel}
          errors={errors}
          label={label}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          maxLength={maxLength}
          {...rest}
        />
      </div>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {isLoading && <DropdownMessage text="Aranıyor..." />}
          
          {isError && <DropdownMessage text="Arama sırasında hata oluştu" isError />}
          
          {!isLoading && !isError && searchResults.length === 0 && debouncedSearchTerm && (
            <DropdownMessage text="Sonuç bulunamadı" />
          )}

          {!isLoading && !isError && searchResults.length > 0 && (
            <div className="py-1">
              {searchResults.map((result, index) => (
                <DropdownItem
                  key={`${result.description}-${index}`}
                  result={result}
                  onSelect={handleResultSelect}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

GtipSearchInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  showLabel: PropTypes.bool,
  label: PropTypes.string,
  errors: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  maxLength: PropTypes.number,
  onValidationChange: PropTypes.func,
};

DropdownMessage.propTypes = {
  text: PropTypes.string.isRequired,
  isError: PropTypes.bool,
};

DropdownItem.propTypes = {
  result: PropTypes.shape({
    showGtip: PropTypes.string.isRequired,
    gtip: PropTypes.string.isRequired,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default GtipSearchInput;
